<template>
  <div
    class="app_layout"
    :class="{
      'is_sidebar_open': sidebarOpen,
    }"
  >
      <!-- ========== 侧边栏区域 ========== -->
    <aside class="app_sidebar">
      <div class="app_sidebar_inner">
        <!-- 左上角标题 -->
        
        <div class="sidebar_menu_banner">
            <!-- :style="{ height: menuBannerH ? `${menuBannerH}px` : undefined }" -->
          <img
            :src="menuBannerUrl"
            class="sidebar_menu_banner_img"
            @error="onMenuBannerError"
          />
        </div>

        <!-- 卡片式菜单容器 -->
        <div class="menu_cards">
          <!-- 顶级菜单：每个菜单一个卡片 -->
          <div v-for="top in topMenus" :key="menuKey(top)" class="menu_card">
            <div class="menu_card_header">
              <text class="menu_card_header_title">{{ top.title }}</text>
            </div>

            <div v-if="top.children.length" class="menu_card_grid">
              <button
                v-for="(node, idx) in flattenNodes(top.children)"
                :key="`${menuKey(node)}:${idx}`"
                type="button"
                class="menu_item"
                :class="{ 'is_active': isActiveNode(top, node) }"
                @click="navigate(node)"
              >
                <div class="menu_item_icon">
                  <CustomIcon
                    v-if="node.icon"
                    :key="`${menuKey(node)}:${isActiveNode(top, node) ? '1' : '0'}`"
                    :config="iconConfig(node.icon, isActiveNode(top, node))"
                    :active="isActiveNode(top, node)"
                  />
                </div>
                <div class="menu_item_title">{{ node.title }}</div>
              </button>
            </div>
          </div>
        </div>

        <div class="sidebar_tools">
          <button type="button" class="theme_toggle_btn" @click="toggleDisplayMode">
            {{ isDark ? '切换浅色模式' : '切换暗黑模式' }}
          </button>
        </div>
      </div>
    </aside>
    <div class="app_main">
      <!-- 顶部标题栏 -->
      <header class="app_header">
        <div class="app_header_left">
          <div class="icon_btn" @click="sidebarOpen = !sidebarOpen">☰</div>
          <div class="breadcrumb">
            <div class="breadcrumb_title">{{ pageTitle }}</div>
          </div>
        </div>
      </header>

      <!-- 页面内容区使用 RouterView 渲染当前路由对应的组件 -->
      <section class="app_content">
        <div class="app_content_inner">
          <RouterView v-slot="{ Component, route: r }">
            <component :is="Component" :key="r.fullPath" />
          </RouterView>
        </div>
      </section>
    </div>
    <!-- 遮罩层 -->
    <div class="app_mask" @click="sidebarOpen = false" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from 'd3components';
import { useSettingStore } from 'd3components';
import CustomIcon from 'd3components/src/components/item-d3/d3/d3_customIcon.vue';
import fallbackMenuBanner from '../../../assets/logo.png';
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const settingStore = useSettingStore();

// ============================
// Sidebar 顶部 Banner（来自 config：s_menu_banner） URL = userStore.serUrl + settingStore.s_menu_banner
const menuBannerUrl = computed(() => `${(userStore as any).serUrl}${(settingStore as any).s_menu_banner}`);
const menuBannerH = computed(() => Math.min((settingStore as any).s_menu_banner_h, (settingStore as any).s_nav_height));

// 图片加载失败直接替换 img.src
const onMenuBannerError = (e: Event) => {
  (e.target as HTMLImageElement).src = fallbackMenuBanner;
};

// 侧边栏是否展开（移动端/窄屏下用于控制抽屉开关）
const sidebarOpen = ref(false);

// ========== 菜单类型与标准化 ==========
type MenuNode = {
  key?: string;
  name: string;
  path?: string;
  redirect?: any;
  type?: number;
  title: string;
  children: MenuNode[];
  icon?: any;
};

// 菜单数据结构：转换为页面需要的最小字段集 type 直接取 menu.meta.type（0=目录/分组，2=页面 保留 name/path/title/icon/children 等核心字段即可
const normalizeMenu = (menu: any): MenuNode => ({
  name: menu.name,
  path: menu?.path,
  redirect: menu?.redirect,
  type: menu?.meta?.type,
  title: menu?.meta?.title || '',
  icon: menu?.meta?.icon,
  children: (menu?.children || []).map(normalizeMenu),
});

// ========== 侧边菜单数据 ==========
const topMenus = computed<MenuNode[]>(() =>
  (() => {
    const real = (((userStore as any).userMenuRouter || []).map(normalizeMenu) as MenuNode[]);
    return real;
  })()
);

// 生成菜单节点唯一 key：用于 v-for / Map 索引
// 使用后端稳定且唯一的 name
const menuKey = (n: MenuNode) => n.name;

// 顶部面包屑标题：优先使用路由 meta.title，否则退回 name/path
const pageTitle = computed(() => route.meta?.title || route.name || route.path);

// 主题模式：依赖 settingStore.displayMode（约定：dark 表示暗色）
const isDark = computed(() => String((settingStore as any).displayMode || '') === 'dark');

// 切换暗黑/浅色：调用 settingStore.toggleDark
const toggleDisplayMode = () => {
  const toggle = (settingStore as any).toggleDark;
  toggle(!isDark.value);
};
//  CustomIcon 组件需要的 attr 结构
//  active 时使用主题色，否则使用普通文字色
const iconConfig = (url: any, active: boolean) => ({
  attr: {
    cic_tp: 1,
    cic_sz: 18,
    cic_url:
      typeof url === 'string'
        ? url
        : url?.attr?.cic_url || url?.url || '',
    cic_c: active ? 'var(--color-primary)' : 'var(--color-text)',
  },
});

// ========== 激活状态判断 ==========
// 菜单节点的 name 与 vue-router 的 route.name 一致
const activeKeysByTop = computed(() => {
  const m = new Map<string, string>();
  const activeName = String(route.name || '');

  for (const top of topMenus.value) {
    const items = top.children?.length ? flattenNodes(top.children) : [];
    const hit = items.find((n) => String(n.name || '') === activeName);
    if (hit) m.set(menuKey(top), menuKey(hit));
  }

  return m;
});

// 某个菜单 node 是否为当前激活项
const isActiveNode = (top: MenuNode, node: MenuNode): boolean =>
  activeKeysByTop.value.get(menuKey(top)) === menuKey(node);

// ========== 导航 ==========
// 点击菜单跳转：
// 直接 push({ name: node.name })
//  跳转后关闭侧边栏
const navigate = (node: MenuNode) => {
  router.push({ name: node.name });
  sidebarOpen.value = false;
};

// 将树形 children 拉平：
// 菜单格渲染时，把两层/多层 children 展开成一维列表 保留父节点本身（如果它也有 children，会出现在展开结果中）
const flattenNodes = (nodes: MenuNode[] = []): MenuNode[] =>
  nodes.flatMap((n) => (n.children?.length ? [n, ...flattenNodes(n.children)] : [n]));
</script>

<style scoped lang="scss">
.app_layout {
  // CSS 变量定义 (可被全局变量覆盖)
  --sidebar-w: 50%;           // 侧边栏宽度
  --header-h: 35px;             // 头部高度
  --bg: var(--color-body);    // 背景色
  --panel: var(--color-body); // 面板背景色
  --border: var(--color-outline, rgba(0, 0, 0, 0.06)); // 边框色
  --shadow: 0 8px 30px rgba(0, 0, 0, 0.06);            // 阴影
  --primary: var(--color-primary);                     // 主题色
  --primary-100: var(--color-primary-container, var(--color-primary)); // 主题色浅色
  --text-1: var(--color-text); // 主文字色
  --text-2: var(--color-text); // 次文字色

  height: 100vh;
  display: flex;
  background: var(--bg);
  overflow: hidden;
}

// ============================================================================
// 侧边栏样式
// ============================================================================
.app_sidebar {
  width: fit-content;
  min-width: 260px;
  max-width: var(--sidebar-w);
  background: var(--panel);
  transition: width 0.18s ease, transform 0.18s ease;
  overflow: hidden;
  flex: 0 0 auto;
  position: relative;
  z-index: 2000;
}

.app_sidebar_inner {
  height: 100%;
  overflow: auto;
  padding: 0 20px 0 20px;
  padding-top: var(--safe-area-inset-top);
  background: var(--bg);
}

.sidebar_menu_banner {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 12px;
  margin-bottom: 12px;
}

.sidebar_menu_banner_img {
  width: 100%;
  display: block;
  object-fit: cover;
  max-height: 120px;
}

// ============================================================================
// 主内容区样式
// ============================================================================
.app_main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.app_header {
  height: calc(var(--header-h) + var(--safe-area-inset-top));
  background: var(--panel);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  padding-top: var(--safe-area-inset-top); 
}

.app_header_left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.breadcrumb_title {
  font-size: 28px;
  font-weight: 600;
  color: var(--text-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60vw;
}

.icon_btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--panel);
  cursor: pointer;
  // 使用 flex 实现水平和垂直居中
  display: flex;
  align-items: center;
  justify-content: center;
}

.app_content {
  flex: 1 1 auto;
  overflow: auto;
  height: calc(100vh - var(--bottom-bar-h));
}

// ============================================================================
// 卡片式菜单样式
// ============================================================================
.menu_cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sidebar_tools {
  padding-top: 12px;
}

.theme_toggle_btn {
  width: 100%;
  border: 1px solid var(--border);
  background: var(--panel);
  color: var(--text-1);
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
}

.theme_toggle_btn:focus,
.theme_toggle_btn:focus-visible,
.theme_toggle_btn:active {
  outline: none;
  box-shadow: none;
}

.menu_card {
  background:var(--color-on-info);
  border-radius: 12px;
  // border: 1px solid var(--border);
  box-shadow: none;
  overflow: hidden;
}

.menu_card_header {
  display: flex;
  align-items: center;
  padding: 14px 16px 10px;
}

.menu_card_header_title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-1);
  line-height: 1.2;
}

.menu_card_grid {
  padding: 12px 12px 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.menu_item {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 10px 8px;
  border-radius: 12px;
  cursor: pointer;
  color: var(--text-2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.15s ease, color 0.15s ease;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
}

.menu_item:focus,
.menu_item:focus-visible,
.menu_item:active {
  outline: none;
  box-shadow: none;
}

.menu_item:hover {
  background: transparent;
}

.menu_item.is_active {
  background: transparent;
  color: var(--text-2);
}

.menu_item.is_active:hover {
  background: transparent;
}

.menu_item_icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu_item_title {
  font-size: 24px;
  text-align: center;
  line-height: 1.25;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
.app_mask {
  display: none;
}
@media (max-width: 992px) {
  .app_layout {
    position: relative;
  }

  // 侧边栏: 绝对定位，默认隐藏在左侧
  .app_sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 2000;
    transform: translateX(-100%); // 默认隐藏
    width: fit-content;
    min-width: 260px;
    max-width: 82vw;
    box-shadow: none;
  }

  // 侧边栏展开时: 滑入显示
  .app_layout.is_sidebar_open .app_sidebar {
    transform: translateX(0);
  }

  // 遮罩层样式
  .app_mask {
    position: fixed;
    inset: 0;
    z-index: 1990;
    background: var(--color-scrim, rgba(0, 0, 0, 0.35));
    display: none;
  }

  // 侧边栏展开时: 显示遮罩
  .app_layout.is_sidebar_open .app_mask {
    display: block;
  }
}
</style>
